import { useCallback } from 'react';
import { InfiniteData, useQueryClient } from 'react-query';
import { queryKeys } from './queries';
import { ListDraftsIO } from './types';

type DraftCacheEntry = ListDraftsIO['output']['drafts'][number];

type DraftListCache = { drafts: Array<DraftCacheEntry> } | undefined;

const isInfiniteData = <T>(data: unknown): data is InfiniteData<T> => {
  return typeof data === 'object' && data !== null && 'pages' in data && Array.isArray((data as InfiniteData<T>).pages);
};

// Static key prefix for all listDrafts cache queries
const listDraftsBaseKey = [...queryKeys.baseKey, 'listDrafts'];

/**
 * Optimistically patches the listDrafts cache with the new group name and fires a
 * fire-and-forget upsert to the BE so the updated name is persisted for reloads.
 *
 * The upsert includes the group-thread-specific fields (groupName, threadType,
 * participantPhones) that the BE requires to correctly store group draft metadata.
 * We call SchemaSMSDraftService.Upsert directly (bypassing the mutation hook) to
 * avoid the mutation's onSuccess cache-rebuild logic from interfering with the
 * optimistic update.
 */
export const useDraftGroupNameSync = ({ threadId }: { threadId: string }) => {
  const queryClient = useQueryClient();

  const syncDraftGroupName = useCallback(
    (newGroupName: string) => {
      // Optimistically patch the listDrafts cache so the UI reflects the new name immediately.
      queryClient.setQueriesData<InfiniteData<ListDraftsIO['output']> | DraftListCache>(
        {
          queryKey: listDraftsBaseKey,
          exact: false,
          predicate: (query) => query.state.data !== undefined,
        },
        (oldData) => {
          if (!oldData) return undefined;
          if (isInfiniteData<ListDraftsIO['output']>(oldData)) {
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                drafts: (page.drafts ?? []).map((draft) =>
                  draft.threadId === threadId ? { ...draft, groupName: newGroupName } : draft
                ),
              })),
            };
          }
          return {
            ...oldData,
            drafts: (oldData.drafts ?? []).map((draft) =>
              draft.threadId === threadId ? { ...draft, groupName: newGroupName } : draft
            ),
          };
        }
      );
    },
    [queryClient, threadId]
  );

  return { syncDraftGroupName };
};
